import { formatMoney } from '../../../app/Libraries/Utils';
import {
  disableScrolling,
  enableScrolling,
  wait,
} from './utils';
import {
  attachCartEventListeners, checkCartIsValid, setCartLoading, confirmCheckout,
} from './cart-functions';

/**
 * DOM reference
 */
const cartDrawer = document.getElementById('js-cart-drawer');

/**
 * Internal state (safe defaults)
 */
type CartState = 'empty' | 'items' | 'checkout';
let cartState: CartState = 'empty';
let lastFocusedElement: HTMLElement | null = null;

let cartPages: NodeListOf<HTMLElement> = [] as any;
let cartCheckoutBtn: HTMLButtonElement | null = null;
let cartCheckoutLink: HTMLAnchorElement | null = null;
let cartSubtotal: HTMLElement | null = null;
let cartDiscountLine: HTMLElement | null = null;
let cartDiscount: HTMLElement | null = null;
let latestCart: any = null;

/**
 * Initialize ONLY if cart drawer exists
 */
if (cartDrawer) {
  initCartDrawer(cartDrawer);
}

/**
 * Init logic (runs once per page)
 */
function initCartDrawer(cartDrawer: HTMLElement) {
  const closeBtns = cartDrawer.querySelectorAll('.js-close-btn');
  const cartToggle = document.querySelectorAll('.js-toggle-cart');
  const cartOverlay = cartDrawer.querySelector('.cart-drawer__overlay');

  cartPages = cartDrawer.querySelectorAll<HTMLElement>('.js-cart-drawer-page');
  cartCheckoutBtn = cartDrawer.querySelector<HTMLButtonElement>('.js-cart-drawer-checkout');
  cartCheckoutLink = cartDrawer.querySelector<HTMLAnchorElement>('.js-cart-drawer-checkout-link');
  cartSubtotal = cartDrawer.querySelector('.js-cart-drawer-total');
  cartDiscountLine = cartDrawer.querySelector('.js-cart-drawer-discount-line');
  cartDiscount = cartDrawer.querySelector('.js-cart-drawer-discount');

  cartToggle.forEach((btn) => btn.addEventListener('click', openCartDrawer));
  closeBtns.forEach((btn) => btn.addEventListener('click', closeCartDrawer));
  cartOverlay?.addEventListener('click', closeCartDrawer);

  cartCheckoutBtn?.addEventListener('click', async () => {
    if (!(await confirmCheckout(latestCart))) {
      return;
    }

    updateCartState('checkout');
  });

  cartCheckoutLink?.addEventListener('click', (e) => {
    if (cartCheckoutLink?.getAttribute('aria-disabled') === 'true') {
      return;
    }

    // The modal confirm is async, so always intercept the link and navigate manually
    // once (and if) the customer proceeds.
    e.preventDefault();
    confirmCheckout(latestCart).then((confirmed) => {
      if (confirmed) {
        const href = cartCheckoutLink?.getAttribute('href');
        if (href) {
          window.location.assign(href);
        }
      }
    });
  });

  document.addEventListener('keyup', (e) => {
    if (e.key !== 'Escape' || !cartDrawer.classList.contains('open')) {
      return;
    }
    // Escape closes the topmost drawer only. The reward selection drawer opens on top of this
    // one and has its own document-level handler, so without this both would close together.
    if (document.getElementById('js-reward-selection-drawer')?.classList.contains('open')) {
      return;
    }
    e.preventDefault();
    closeCartDrawer();
  });
}

/**
 * Open cart drawer
 */
export function openCartDrawer() {
  if (!cartDrawer) {
    return;
  }

  lastFocusedElement = document.activeElement as HTMLElement;

  cartDrawer.classList.add('open');
  cartDrawer.removeAttribute('aria-hidden');
  cartDrawer.removeAttribute('inert');
  disableScrolling();

  document.dispatchEvent(new Event('cart:opened'));

  cartDrawer.focus({ preventScroll: true });
}

/**
 * Close cart drawer
 */
export async function closeCartDrawer() {
  if (!cartDrawer) {
    return;
  }

  cartDrawer.classList.remove('open');
  cartDrawer.setAttribute('inert', '');
  cartDrawer.setAttribute('aria-hidden', 'true');
  enableScrolling();

  if (cartState === 'checkout') {
    setTimeout(() => updateCartState('items'), 600);
  }

  const pageBtns = document.querySelectorAll<HTMLElement>(`
    select[data-tabindex="modified"],
    input[data-tabindex="modified"],
    textarea[data-tabindex="modified"],
    button[data-tabindex="modified"],
    a[data-tabindex="modified"],
    iframe[data-tabindex="modified"]
  `);

  pageBtns.forEach((btn) => {
    btn.setAttribute('tabindex', '0');
    btn.removeAttribute('data-tabindex');
  });

  const tabbables = cartDrawer.querySelectorAll<HTMLElement>('[data-tabindex="modified"]');
  tabbables.forEach((el) => {
    el.setAttribute('tabindex', '-1');
    el.removeAttribute('data-tabindex');
  });

  if (lastFocusedElement) {
    lastFocusedElement.focus({ preventScroll: true });
    lastFocusedElement = null;
  }

  await wait(600);

  cartDrawer
    .querySelectorAll<HTMLElement>('.js-cart-item')
    .forEach(clearQtyWarning);
}

/**
 * Cart state handling
 */
export function updateCartState(state: CartState) {
  cartState = state;

  cartPages.forEach((page) => {
    const isActive = page.dataset.state === cartState;
    page.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  });
}

/**
 * Update full cart
 */
export function updateCart(cartObject: any) {
  if (!cartDrawer) {
    return;
  }

  latestCart = cartObject;

  if (!cartObject?.cart?.items || Object.keys(cartObject.cart.items).length === 0) {
    updateCartState('empty');
    return;
  }

  if (cartObject.html) {
    const cartItemsWrapper = cartDrawer.querySelector('.js-cart-drawer-items') as HTMLElement;
    cartItemsWrapper.innerHTML = '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(cartObject.html, 'text/html');

    Array.from(doc.body.children).forEach((el) => {
      cartItemsWrapper.appendChild(el);
    });

    attachCartEventListeners(cartDrawer, 'compact');
    setCartLoading(false);
  }

  const isValid = checkCartIsValid(cartObject);

  if (cartCheckoutBtn) {
    cartCheckoutBtn.disabled = !isValid;
  }

  if (cartCheckoutLink) {
    const checkoutLink = cartCheckoutLink;

    if (!isValid) {
      checkoutLink.setAttribute('aria-disabled', 'true');
      checkoutLink.classList.add('is-disabled');

      checkoutLink.addEventListener('click', (e) => {
        if (checkoutLink.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    } else {
      checkoutLink.removeAttribute('aria-disabled');
      checkoutLink.classList.remove('is-disabled');
    }
  }

  updateCartTotal(cartObject);
  updateCartState('items');
}

/**
 * Messages
 */
export function setCartMessage(message: string) {
  const messageElement = cartDrawer?.querySelector('.js-cart-success-msg');
  if (!messageElement) {
    return;
  }

  messageElement.innerHTML = message;
  messageElement.removeAttribute('aria-hidden');

  setTimeout(() => {
    messageElement.setAttribute('aria-hidden', 'true');
  }, 5000);
}

/**
 * Totals
 */
export function updateCartTotal(cartObject: any) {
  if (!cartSubtotal) {
    return;
  }
  // Estimated total stays the charged (post-discount) subtotal; the budget-receive saving is
  // shown below it as a "Discount received" line, mirroring the cart page summary.
  cartSubtotal.innerHTML = formatMoney(cartObject.cart.subTotal || 0);
  const discountTotal = cartObject.cart.discountTotal ?? 0;
  if (cartDiscountLine && cartDiscount) {
    cartDiscount.innerHTML = `-${formatMoney(discountTotal)}`;
    cartDiscountLine.classList.toggle('is-hidden', discountTotal <= 0);
  }
}

/**
 * Update single item
 */
export function updateCartItem(cartObject: any, cartItemId: string) {
  if (!cartDrawer) {
    return;
  }

  const cartItem = cartDrawer.querySelector<HTMLElement>(
    `.js-cart-item[data-cart-item-id="${cartItemId}"]`
  );

  const item = cartObject.cart.items[cartItemId];
  if (!item || !cartItem) {
    return;
  }

  const price = item.priceOverride ?? item.price.discount ?? item.price.base;
  // The struck price is what the customer would otherwise pay: promo-priced rows
  // reference today's catalogue price (sale if one exists); plain sale rows reference base.
  const basePrice = (item.priceOverride !== null && item.priceOverride !== undefined)
    ? (item.price.discount ?? item.price.base)
    : item.price.base;

  clearQtyWarning(cartItem);

  cartItem.querySelectorAll('.js-cart-item-price').forEach((el) => {
    el.innerHTML = formatMoney(price * item.quantity);
  });

  cartItem.querySelectorAll('.js-cart-item-base-price').forEach((el) => {
    el.innerHTML = formatMoney(basePrice * item.quantity);
  });
}

/**
 * Helpers
 */
function clearQtyWarning(cartItemEl: HTMLElement) {
  const warning = cartItemEl.querySelector('[data-warning="qty"]');
  warning?.remove();
}
